import { asBrokerCode, type BrokerCode } from '@selogate/core'
import { stopCameraStream } from './camera'

export interface QrScanSession {
  stop(): void
}

interface ReaderControls {
  stop(): void
}

interface ReaderResult {
  getText(): string
}

interface QrReader {
  decodeFromStream(
    stream: MediaStream,
    video: HTMLVideoElement,
    callback: (result: ReaderResult | undefined, error: unknown, controls: ReaderControls) => void,
  ): Promise<ReaderControls>
}

type QrReaderLoader = () => Promise<{ readonly createReader: () => QrReader }>

async function loadQrReader(): Promise<{ readonly createReader: () => QrReader }> {
  const { BrowserQRCodeReader } = await import('@zxing/browser')
  return { createReader: () => new BrowserQRCodeReader() }
}

export function brokerCodeFromQr(raw: string): BrokerCode | undefined {
  try {
    return asBrokerCode(raw)
  } catch {
    return undefined
  }
}

export interface QrScanOptions {
  /**
   * Cancels a scan that has not finished starting.
   *
   * Without it the only stop handle is the resolved `QrScanSession`, so a caller that cancels
   * between `getUserMedia()` resolving and the decoder becoming ready cannot reach the camera at
   * all — and that window is not short: it spans the lazy decoder chunk's download *and* ZXing's
   * internal `video.play()`, which does not resolve while the document is hidden. Backgrounding
   * the tab right after granting the camera therefore left the capture light on until the user
   * came back, which is exactly the case the visibility handler exists to prevent.
   */
  readonly signal?: AbortSignal
  readonly loader?: QrReaderLoader
}

/** Rejects as soon as `signal` aborts, so a pending start unwinds into the caller's cleanup. */
function raceAbort<T>(work: Promise<T>, signal: AbortSignal | undefined): Promise<T> {
  if (signal === undefined) return work
  const aborted = (): DOMException => new DOMException('QR scan aborted', 'AbortError')
  if (signal.aborted) return Promise.reject(aborted())
  return Promise.race([
    work,
    new Promise<never>((_resolve, reject) => {
      signal.addEventListener('abort', () => reject(aborted()), { once: true })
    }),
  ])
}

/**
 * Starts local video decoding and owns both decoder and camera-stream cleanup — including
 * before it has resolved, provided the caller passes `options.signal`.
 */
export async function startQrScan(
  stream: MediaStream,
  video: HTMLVideoElement,
  onDecoded: (value: string) => void,
  options: QrScanOptions = {},
): Promise<QrScanSession> {
  const { signal, loader = loadQrReader } = options
  let stopped = false
  let controls: ReaderControls | undefined
  let decoderStopped = false

  const stop = (callbackControls?: ReaderControls): void => {
    if (stopped) return
    stopped = true
    const activeControls = callbackControls ?? controls
    if (activeControls !== undefined) {
      activeControls.stop()
      decoderStopped = true
    }
    stopCameraStream(stream)
  }

  try {
    const { createReader } = await raceAbort(loader(), signal)
    const decoding = createReader().decodeFromStream(stream, video, (result, _error, callbackControls) => {
      if (result === undefined || stopped) return
      onDecoded(result.getText())
      stop(callbackControls)
    })
    // An aborted start abandons this promise, so the decoder can still become ready with nobody
    // holding its controls. Stop it whenever it arrives late rather than leaving the loop running.
    void decoding.then(
      (late) => {
        if (!stopped || decoderStopped) return
        decoderStopped = true
        late.stop()
      },
      () => {},
    )
    controls = await raceAbort(decoding, signal)
    if (stopped && !decoderStopped) controls.stop()
    return { stop }
  } catch (error) {
    stop()
    throw error
  }
}
